import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getCoreApiOverrideInfo } from "../utils/coreApiOverride";
import {
  resolveCompanyTenantIdFromRaw,
  getStagingHostnameDefaultTenantId,
  getSingleCompanyTenantIdOverride,
  isLocalLoopbackHostname,
} from "../utils/companyTenantResolver";

// Token getter function - will be set by Auth0Provider wrapper
let tokenGetter = null;
let tokenGetterReady = false;

export const setTokenGetter = (getter) => {
  tokenGetter = getter;
  tokenGetterReady = !!getter;
  console.log('🔍 setTokenGetter called, tokenGetterReady:', tokenGetterReady);
};

// Export tokenGetter for use in components
export const getTokenGetter = () => tokenGetter;

export const isTokenGetterReady = () => {
  return tokenGetterReady && !!tokenGetter;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForTokenGetter = async (maxWaitMs = 2500, pollMs = 50) => {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    if (tokenGetter) return tokenGetter;
    if (isTokenGetterReady()) break;
    await sleep(pollMs);
  }
  return tokenGetter;
};

// Helper function to wait for Auth0 functions to be available (if using Auth0)
export const waitForAuthFunctions = async (maxWaitTime = 5000) => {
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitTime) {
    // For now, we'll use direct AWS credentials in development
    // In production, this would check for Auth0 functions
    return true;
  }
  
  return false;
};

// Get API URL based on current environment
// Simple hostname-based detection for centralized API Gateway
export const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    
    // Staging environment
    if (hostname === 'staging.krossglobalmarketing.com' || hostname.includes('staging')) {
      return 'https://api-staging.krossglobalmarketing.com';
    }
    
    // Production environment
    if (hostname === 'krossglobalmarketing.com' || hostname === 'www.krossglobalmarketing.com') {
      return 'https://api.krossglobalmarketing.com';
    }
    
    // Local development - use staging API
    if (isLocalLoopbackHostname(hostname)) {
      return 'https://api-staging.krossglobalmarketing.com';
    }
  }
  
  // Default fallback to staging
  return 'https://api-staging.krossglobalmarketing.com';
};

/** Base URL for company signup (`serverless-signup.yml` satellite stack). Baked at build from REACT_APP_SIGNUP_API_URL. */
export const getSignupApiUrl = () => {
  const raw =
    typeof process !== 'undefined' && process.env && process.env.REACT_APP_SIGNUP_API_URL
      ? String(process.env.REACT_APP_SIGNUP_API_URL).trim()
      : '';
  if (raw) return raw.replace(/\/+$/, '');
  return getApiUrl();
};

// Core API URL (Path B: kg-api-core at /core). Used for domains list, emails, health when split is active.
export const getCoreApiUrl = () => {
  const override = getCoreApiOverrideInfo();
  if (override?.active && override?.coreBaseUrl) {
    return override.coreBaseUrl;
  }
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const hostname = window.location.hostname;
    if (hostname === 'staging.krossglobalmarketing.com' || hostname.includes('staging')) {
      // Keep staging aligned with production-style core routing to avoid split-path drift.
      return 'https://core2-staging.krossglobalmarketing.com/core';
    }
    if (hostname === 'krossglobalmarketing.com' || hostname === 'www.krossglobalmarketing.com') {
      // NOTE: api.krossglobalmarketing.com is an EDGE custom domain and cannot attach HTTP APIs.
      // Core cutover uses the production HTTP API custom domain instead.
      return 'https://core2.krossglobalmarketing.com/core';
    }
    if (isLocalLoopbackHostname(hostname)) {
      // Local "Run" should mirror staging/prod core behavior by default.
      return 'https://core2-staging.krossglobalmarketing.com/core';
    }
  }
  // Safe default for unknown hosts follows staging core custom domain.
  return 'https://core2-staging.krossglobalmarketing.com/core';
};

// Outbound API URL for send + SMTP.com credentials (split stack). When set, use for outbound/emails/send and domains/.../outbound/smtpcom-credentials.
// Fallback: when env is unset (e.g. old bundle or build without env), use hostname-based outbound path so prod/staging still hit /outbound.
export const getOutboundApiUrl = () => {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_OUTBOUND_API_URL) {
    return process.env.REACT_APP_OUTBOUND_API_URL;
  }
  // Hostname-based fallback so send always uses outbound even if env wasn't baked in
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const hostname = window.location.hostname;
    if (hostname === 'staging.krossglobalmarketing.com' || hostname.includes('staging')) {
      return 'https://api-staging.krossglobalmarketing.com/outbound';
    }
    if (hostname === 'krossglobalmarketing.com' || hostname === 'www.krossglobalmarketing.com') {
      return 'https://api.krossglobalmarketing.com/outbound';
    }
    if (isLocalLoopbackHostname(hostname)) {
      return 'https://api-staging.krossglobalmarketing.com/outbound';
    }
  }
  return getApiUrl();
};

// CRM API URL (/crm base path -> dedicated kg-api-crm stack).
export const getCrmApiUrl = () => {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_CRM_API_URL) {
    return process.env.REACT_APP_CRM_API_URL;
  }
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const hostname = window.location.hostname;
    if (hostname === 'staging.krossglobalmarketing.com' || hostname.includes('staging')) {
      return 'https://api-staging.krossglobalmarketing.com/crm';
    }
    if (hostname === 'krossglobalmarketing.com' || hostname === 'www.krossglobalmarketing.com') {
      return 'https://api.krossglobalmarketing.com/crm';
    }
    if (isLocalLoopbackHostname(hostname)) {
      return 'https://api-staging.krossglobalmarketing.com/crm';
    }
  }
  return 'https://api-staging.krossglobalmarketing.com/crm';
};

// ERM API URL (/erm base path -> dedicated kg-api-erm stack).
export const getErmApiUrl = () => {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_ERM_API_URL) {
    return process.env.REACT_APP_ERM_API_URL;
  }
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const hostname = window.location.hostname;
    if (hostname === 'staging.krossglobalmarketing.com' || hostname.includes('staging')) {
      return 'https://api-staging.krossglobalmarketing.com/erm';
    }
    if (hostname === 'krossglobalmarketing.com' || hostname === 'www.krossglobalmarketing.com') {
      return 'https://api.krossglobalmarketing.com/erm';
    }
    if (isLocalLoopbackHostname(hostname)) {
      return 'https://api-staging.krossglobalmarketing.com/erm';
    }
  }
  return 'https://api-staging.krossglobalmarketing.com/erm';
};

// Executive Assistant API (/executive-assistant base path -> kg-api-executive-assistant).
export const getExecutiveAssistantApiUrl = () => {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_EXECUTIVE_ASSISTANT_API_URL) {
    return process.env.REACT_APP_EXECUTIVE_ASSISTANT_API_URL;
  }
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const hostname = window.location.hostname;
    if (hostname === 'staging.krossglobalmarketing.com' || hostname.includes('staging')) {
      return 'https://api-staging.krossglobalmarketing.com/executive-assistant';
    }
    if (hostname === 'krossglobalmarketing.com' || hostname === 'www.krossglobalmarketing.com') {
      return 'https://api.krossglobalmarketing.com/executive-assistant';
    }
    // CRA dev server: same-origin + src/setupProxy.js forwards to REACT_APP_EA_PROXY_TARGET (default staging API).
    if (isLocalLoopbackHostname(hostname)) {
      return `${window.location.origin}/executive-assistant`;
    }
  }
  return 'https://api-staging.krossglobalmarketing.com/executive-assistant';
};

/** Exported for dev parity probe; same logic RTK uses for EA `X-Kg-Tenant-Id` when no explicit tenant on the arg. */
export const getActiveTenantIdFallback = () => {
  if (typeof window === 'undefined') return '';
  try {
    const single = getSingleCompanyTenantIdOverride();
    if (single) {
      console.log('[TENANT-TRACE] getActiveTenantIdFallback -> override:', single);
      return single;
    }

    const fromWorkspace = String(window.localStorage?.getItem('selectedCompanyTenantId') || '').trim();
    if (fromWorkspace && fromWorkspace.toLowerCase() !== 'all') {
      const resolved = resolveCompanyTenantIdFromRaw(fromWorkspace, null) || fromWorkspace;
      console.log('[TENANT-TRACE] getActiveTenantIdFallback -> fromWorkspace:', fromWorkspace, '-> resolved:', resolved);
      return resolved;
    }
    const fromActive = String(window.localStorage?.getItem('activeTenantId') || '').trim();
    if (fromActive && fromActive.toLowerCase() !== 'all') {
      return resolveCompanyTenantIdFromRaw(fromActive, null) || fromActive;
    }
    const fromSelected = String(window.localStorage?.getItem('selectedAccountId') || '').trim();
    if (fromSelected && fromSelected.toLowerCase() !== 'all') {
      return resolveCompanyTenantIdFromRaw(fromSelected, null) || fromSelected;
    }
  } catch {
    // Ignore localStorage read errors and fall back to unscoped request.
  }
  const hostTenant = getStagingHostnameDefaultTenantId();
  if (hostTenant) return hostTenant;
  return '';
};

const resolveTenantId = (arg) => {
  if (arg && typeof arg === 'object') {
    const explicit = String(arg.tenantId || arg.accountId || '').trim();
    if (explicit && explicit.toLowerCase() !== 'all') return explicit;
  }
  return getActiveTenantIdFallback();
};

/** Paths served only by kg-api-executive-assistant (must not hit main/Core/CRM base URLs). */
const EA_RELATIVE_PATH_PREFIXES = [
  '/assistants',
  '/assistant-tasks',
  '/memory',
  '/activity',
  '/scheduled-emails',
  '/owner-sending-preferences',
  '/schedule-message-scripts',
];

function isExecutiveAssistantPath(url) {
  if (typeof url !== 'string' || !url.startsWith('/')) return false;
  return EA_RELATIVE_PATH_PREFIXES.some((p) => {
    if (url === p) return true;
    if (url.startsWith(`${p}/`)) return true;
    if (url.startsWith(`${p}?`)) return true;
    return false;
  });
}

/**
 * Tenant for EA HTTP calls: query string on `url`, JSON body, then active account.
 * Sent as X-Kg-Tenant-Id on every EA request so Lambdas always receive tenantId.
 */
function resolveTenantIdForHttpArgs(args) {
  const url = typeof args === 'string' ? args : args?.url;
  if (typeof url === 'string') {
    const qIndex = url.indexOf('?');
    if (qIndex >= 0) {
      const params = new URLSearchParams(url.slice(qIndex + 1));
      const t = String(params.get('tenantId') || params.get('accountId') || '').trim();
      if (t && t.toLowerCase() !== 'all') return t;
    }
  }
  if (args && typeof args === 'object' && args.body != null) {
    try {
      const raw = args.body;
      const b = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (b && typeof b === 'object') {
        const t = String(b.tenantId || b.accountId || '').trim();
        if (t && t.toLowerCase() !== 'all') return t;
      }
    } catch {
      /* non-JSON body */
    }
  }
  return getActiveTenantIdFallback();
}

// Pick base URL per request: outbound for send + smtpcom; core for Unibox/domains; crm for CRM routes; main API for the rest.
// Send-pools API is at /send-pools on main API. Core API (kg-api-core) is at /core when Path B split is active.
function getBaseUrlForRequest(args) {
  const url = typeof args === 'string' ? args : args?.url;
  const method = typeof args === 'string' ? 'GET' : String(args?.method || 'GET').toUpperCase();
  if (typeof url !== 'string') return getApiUrl();
  if (url.startsWith('/signup/')) return getSignupApiUrl();
  if (url.includes('outbound/emails/send') || url.includes('outbound/smtpcom-credentials') || url.includes('outbound/mailgun-credentials')) return getOutboundApiUrl();
  if (url.includes('emails/send')) return getOutboundApiUrl(); // legacy path on outbound stack
  if (/^\/rules-engine(\/|\?|$)/.test(url)) return getCrmApiUrl();
  // CRM accounts = org records (Dynamo kross-global-accounts), not /aws-accounts on Core.
  // target-accounts = CRM Target Accounts (prospects), separate entity in kg-api-crm stack.
  if (/^\/(leads|clients|contacts|deals|meetings|accounts|target-accounts)(\/|\?|$)/.test(url)) return getCrmApiUrl();
  if (/^\/(projects|tasks)(\/|\?|$)/.test(url)) return getErmApiUrl();
  if (isExecutiveAssistantPath(url)) return getExecutiveAssistantApiUrl();
  // Path B moved routes: read/list + Unibox live on kg-api-core at /core. Do not collapse back to one base URL.
  // Domains list reads are served by Core; writes stay on main API.
  if (url === '/domains' || url.startsWith('/domains?')) {
    return method === 'GET' ? getCoreApiUrl() : getApiUrl();
  }
  if (
    url === '/health' ||
    url === '/emails' ||
    url.startsWith('/emails?') ||
    url === '/emails/update' ||
    url === '/emails/original' ||
    url.startsWith('/emails/original?') ||
    url === '/emails/permanent-delete' ||
    url === '/emails/purge-trash'
  ) return getCoreApiUrl();
  // Email Accounts: reads are served by Core; writes still live on main API.
  // This avoids POST /core/email-accounts 404 on prod/staging custom domains.
  if (url === '/email-accounts' || url.startsWith('/email-accounts/')) {
    return method === 'GET' ? getCoreApiUrl() : getApiUrl();
  }
  // Unibox + Domains: AWS accounts currently live on Core.
  if (url.startsWith('/aws-accounts') || url === '/aws-accounts') return getCoreApiUrl();
  // Company Profile (logo upload) lives on Core API as well
  if (url.startsWith('/company-profile/')) return getCoreApiUrl();
  // send-pools and all other /domains/:domain/* stay on main API
  return getApiUrl();
}

const baseQueryWithRetry = async (args, api, extraOptions) => {
  // Wait for auth functions to be available
  await waitForAuthFunctions();
  
  const baseUrl = getBaseUrlForRequest(args);
  const executiveAssistantApiUrl = getExecutiveAssistantApiUrl();
  const normBase = (u) => String(u || '').replace(/\/+$/, '');
  const isEaRequest = normBase(baseUrl) === normBase(executiveAssistantApiUrl);
  // Always resolve a tenant id for tenant-guarded endpoints. The Core (kg-api-core
  // canary), main API, and CRM stacks all run `enforceTenantAccess`, which returns
  // 403 `missing_tenant` when `X-Kg-Tenant-Id` is absent. Previously this was only
  // set for Executive Assistant requests, which caused empty Unibox / 0 emails /
  // missing prefs after the canary tenant guard was hardened on 2026-05-05.
  const eaTenantHeader = isEaRequest ? resolveTenantIdForHttpArgs(args) : '';
  const httpTenantHeader = String(resolveTenantIdForHttpArgs(args) || '').trim();

  console.log('🔍 API Debug - Starting request:', args);
  console.log('🔍 API Debug - Endpoint:', args?.url || 'unknown');
  console.log('🔍 API Debug - Base URL:', baseUrl);
  console.log('🔍 API Debug - TokenGetter available:', !!tokenGetter);
  
  const isOutboundRequest = baseUrl === getOutboundApiUrl() && getOutboundApiUrl() !== getApiUrl();
  let result = await fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers, { endpoint }) => {
      try {
        console.log('🔍 API Debug - prepareHeaders called for:', endpoint);
        
        // Skip auth for health check endpoints
        if (endpoint === 'testAWSConnection' || endpoint === 'health') {
          console.log('🔍 API Debug - Skipping auth for health check endpoint');
          return headers;
        }

        // Get Auth0 access token for all other endpoints
        const tg = tokenGetter || (await waitForTokenGetter());
        if (tg) {
          try {
            console.log('🔍 API Debug - Calling tokenGetter...');
            const token = await tg();
            console.log('🔍 API Debug - TokenGetter returned:', token ? `Token (${token.substring(0, 20)}...)` : 'null/undefined');
            
            if (token) {
              headers.set('Authorization', `Bearer ${token}`);
              console.log('✅ API Debug - Token added to request for:', endpoint);
              console.log('✅ API Debug - Authorization header:', `Bearer ${token.substring(0, 20)}...`);
            } else {
              console.warn('⚠️ API Debug - No token available for:', endpoint);
            }
          } catch (error) {
            console.error("❌ API Debug - Auth Error:", error);
            console.error("❌ API Debug - Error details:", error.message);
            console.error("❌ API Debug - Error stack:", error.stack);
            // If it's a refresh token error, the Auth0Wrapper will handle logout
            if (error.message && error.message.includes('Missing Refresh Token')) {
              console.warn('⚠️ Refresh token expired. User will be logged out.');
            }
            // Continue without token - API will return 401 if auth required
          }
        } else {
          console.warn('⚠️ API Debug - No tokenGetter available for:', endpoint);
        }

        // Re-resolve the tenant id at the moment headers are being prepared so any
        // late update from AccountContext (e.g. /signup/status finishing while the
        // Auth0 token was being fetched) is reflected. EA path keeps its own var
        // for backwards compatibility with [TENANT-TRACE] logging.
        if (eaTenantHeader) {
          headers.set('X-Kg-Tenant-Id', eaTenantHeader);
          console.log('[TENANT-TRACE] X-Kg-Tenant-Id set to:', eaTenantHeader, 'for:', endpoint);
        } else if (endpoint !== 'testAWSConnection' && endpoint !== 'health') {
          const liveTenant = String(resolveTenantIdForHttpArgs(args) || '').trim() || httpTenantHeader;
          if (liveTenant) {
            headers.set('X-Kg-Tenant-Id', liveTenant);
            console.log('[TENANT-TRACE] X-Kg-Tenant-Id set to:', liveTenant, 'for:', endpoint);
          } else {
            console.warn('[TENANT-TRACE] missing X-Kg-Tenant-Id for endpoint:', endpoint, 'path:', args?.url || args);
          }
        }

        return headers;
      } catch (error) {
        console.error("❌ API Debug - prepareHeaders Error:", error);
        return headers;
      }
    },
  })(args, api, extraOptions);

  // Hidden debug log when request went to outbound stack (for debugging across two gateways).
  if (isOutboundRequest) {
    const path = typeof args === 'string' ? args : args?.url;
    const requestId = result.meta?.requestId ?? result?.meta?.response?.headers?.get?.('x-amzn-RequestId') ?? 'n/a';
    console.debug('outboundApiUsed: true, path:', path, 'requestId:', requestId);
  }

  console.log('🔍 API Debug - Request result:', {
    status: result.error?.status || result.data ? 'success' : 'unknown',
    error: result.error ? {
      status: result.error.status,
      data: result.error.data
    } : null,
    endpoint: args?.url || 'unknown',
    method: args?.method || 'GET'
  });
  
  // Enhanced logging for updateEmail endpoint
  if (args?.url?.includes('/emails/update')) {
    console.log('🔍 API Debug - updateEmail request details:', {
      url: args.url,
      method: args.method,
      body: args.body,
      resultStatus: result.error?.status || (result.data ? 'success' : 'unknown'),
      resultData: result.data,
      resultError: result.error
    });
  }

  // Handle 400 Bad Request errors
  if (result.error && result.error.status === 400) {
    console.error("❌ API Debug - 400 Bad Request detected:", {
      endpoint: args?.url || 'unknown',
      error: result.error.data,
      requestBody: args?.body,
      requestMethod: args?.method || 'GET'
    });
    // Log detailed error information for debugging
    if (result.error.data) {
      console.error("❌ Bad Request Details:", JSON.stringify(result.error.data, null, 2));
    }
  }

  // Retry logic for failed requests
  if (result.error && result.error.status === 401) {
    console.log("🔄 API Debug - 401 Unauthorized detected. This may indicate an expired session.");
    // Check if it's a token refresh issue
    if (result.error?.data?.message?.includes('token') || result.error?.data?.message?.includes('expired')) {
      console.warn("⚠️ Token appears to be expired. User may need to re-authenticate.");
      // Note: Auth0Wrapper will handle the logout/redirect on next token request
    }
  }

  return result;
};

// API slice following krosskast pattern
export const marketingApi = createApi({
  reducerPath: 'marketingApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: [
    'S3',
    'Domains',
    'ForwardingDestinations',
    'EmailAccounts',
    'Users',
    'SignupStatus',
    'Emails',
    'SendPools',
    'DomainRouting',
    'ErmProjects',
    'ErmTasks',
    'Accounts',
    'TargetAccounts',
    'Leads',
    'Clients',
    'Contacts',
    'Deals',
    'Meetings',
    'EaAssistants',
    'EaTasks',
    'EaMemory',
    'EaActivity',
    'EaScheduledEmails',
    'EaOwnerSendingPreferences',
    'EaScheduleMessageScripts',
  ],
  endpoints: (builder) => ({
    // S3 Endpoints
    checkS3Bucket: builder.query({
      query: () => '/aws/test-connection',
      providesTags: ['S3'],
      // Extract S3 bucket info from AWS connection test
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return {
            success: true,
            data: {
              exists: response.data.s3Buckets > 0,
              bucketName: 'krossglobal-marketing-emails',
              bucketCount: response.data.s3Buckets,
              lastChecked: response.data.timestamp
            }
          };
        }
        return {
          success: false,
          data: {
            exists: false,
            bucketName: 'krossglobal-marketing-emails',
            bucketCount: 0
          }
        };
      }
    }),

    storeEmailContent: builder.mutation({
      query: ({ key, content, contentType }) => ({
        url: '/aws/s3/store-email',
        method: 'POST',
        body: { key, content, contentType }
      }),
      invalidatesTags: ['S3']
    }),

    getEmailContent: builder.query({
      query: (key) => `/aws/s3/get-email/${key}`,
      providesTags: ['S3']
    }),

    // Email receiving
    getEmails: builder.query({
      query: ({ emailAccount, limit, lastKey, folder, folderAliases, tenantId, search, direction }) => {
        const params = new URLSearchParams();
        if (emailAccount) params.append('emailAccount', emailAccount);
        if (limit) params.append('limit', limit.toString());
        if (lastKey) params.append('lastKey', lastKey);
        if (folder) params.append('folder', folder);
        if (Array.isArray(folderAliases) && folderAliases.length > 0) {
          params.append('folderAliases', JSON.stringify(folderAliases));
        }
        if (tenantId) params.append('tenantId', tenantId);
        if (search) params.append('search', search);
        // Phase A.1 (2026-05-14): direction modifier for the Warm Up
        // system folder. Backend only honors it when folder=Warm Up;
        // for any other folder the param is silently ignored, so it's
        // safe to always forward when the caller supplies it.
        if (direction) params.append('direction', direction);
        return `/emails?${params.toString()}`;
      },
      providesTags: ['Emails']
    }),
    getEmailSentStats: builder.query({
      query: ({ emailAccount, accountId, startToday, startWeek, startMonth, endAt }) => {
        const params = new URLSearchParams();
        params.append('statsOnly', 'sentSummary');
        if (emailAccount) params.append('emailAccount', emailAccount);
        if (accountId) params.append('accountId', accountId);
        if (startToday) params.append('startToday', startToday);
        if (startWeek) params.append('startWeek', startWeek);
        if (startMonth) params.append('startMonth', startMonth);
        if (endAt) params.append('endAt', endAt);
        return `/emails?${params.toString()}`;
      }
    }),
    getEmailOriginal: builder.query({
      query: ({ emailAccount, emailId, unifiedPk, unifiedSk }) => {
        const params = new URLSearchParams();
        if (emailAccount) params.append('emailAccount', emailAccount);
        if (emailId) params.append('emailId', emailId);
        if (unifiedPk) params.append('unifiedPk', unifiedPk);
        if (unifiedSk) params.append('unifiedSk', unifiedSk);
        return `/emails/original?${params.toString()}`;
      }
    }),
    getEmailAttachment: builder.query({
      query: ({ emailAccount, emailId, unifiedPk, unifiedSk, attachmentS3Key }) => {
        const params = new URLSearchParams();
        if (emailAccount) params.append('emailAccount', emailAccount);
        if (emailId) params.append('emailId', emailId);
        if (unifiedPk) params.append('unifiedPk', unifiedPk);
        if (unifiedSk) params.append('unifiedSk', unifiedSk);
        if (attachmentS3Key) params.append('attachmentS3Key', attachmentS3Key);
        return `/emails/original?${params.toString()}`;
      }
    }),

    // Domain Management
    getDomains: builder.query({
      query: () => '/domains',
      providesTags: ['Domains']
    }),

    // Send Pools / Domain Routing (Outbound routing control plane)
    getSendPools: builder.query({
      query: () => '/send-pools/pools',
      providesTags: ['SendPools']
    }),

    getSendPoolProviders: builder.query({
      query: () => '/send-pools/providers',
      providesTags: ['SendPools']
    }),

    getSendPoolEndpoints: builder.query({
      query: (poolId) => `/send-pools/pools/${encodeURIComponent(poolId)}/endpoints`,
      providesTags: (_res, _err, poolId) => [{ type: 'SendPools', id: `endpoints:${String(poolId || '')}` }]
    }),

    setSendPoolHealth: builder.mutation({
      query: ({ poolId, state }) => ({
        url: `/send-pools/pools/${encodeURIComponent(poolId)}/health`,
        method: 'PUT',
        body: { state }
      }),
      invalidatesTags: ['SendPools']
    }),

    createSendPool: builder.mutation({
      query: (body) => ({
        url: '/send-pools/pools',
        method: 'POST',
        body
      }),
      invalidatesTags: ['SendPools']
    }),

    updateSendPool: builder.mutation({
      query: ({ poolId, name, purpose, relayAccountSuffix }) => ({
        url: `/send-pools/pools/${encodeURIComponent(poolId)}`,
        method: 'PUT',
        body: { name, purpose, relayAccountSuffix }
      }),
      invalidatesTags: ['SendPools']
    }),

    deleteSendPool: builder.mutation({
      query: (poolId) => ({
        url: `/send-pools/pools/${encodeURIComponent(poolId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['SendPools']
    }),

    getPoolCredentials: builder.query({
      query: (poolId) => `/send-pools/pools/${encodeURIComponent(poolId)}/credentials`,
      providesTags: (_res, _err, poolId) => [{ type: 'SendPools', id: `credentials:${String(poolId || '')}` }]
    }),
    putPoolCredentials: builder.mutation({
      query: ({ poolId, username, password, domain, relayAccountSuffix }) => ({
        url: `/send-pools/pools/${encodeURIComponent(poolId)}/credentials`,
        method: 'PUT',
        body: { username, password, domain, relayAccountSuffix }
      }),
      invalidatesTags: (_res, _err, { poolId }) => [
        'SendPools',
        { type: 'SendPools', id: `credentials:${String(poolId || '')}` }
      ]
    }),
    revealPoolCredentials: builder.mutation({
      query: (poolId) => ({
        url: `/send-pools/pools/${encodeURIComponent(poolId)}/credentials/reveal`,
        method: 'POST',
        body: {}
      })
    }),

    setDomainFreezeUntil: builder.mutation({
      query: ({ domain, messageClass, freezeUntil }) => ({
        url: `/send-pools/domains/${encodeURIComponent(domain)}/freeze`,
        method: 'PUT',
        body: { messageClass, freezeUntil }
      }),
      invalidatesTags: (_res, _err, args) => [
        'SendPools',
        { type: 'DomainRouting', id: String(args?.domain || '') }
      ]
    }),

    getDomainRoutingPolicy: builder.query({
      query: (domain) => `/send-pools/domains/${encodeURIComponent(domain)}/routing`,
      providesTags: (_res, _err, domain) => [{ type: 'DomainRouting', id: String(domain || '') }]
    }),

    setDomainRoute: builder.mutation({
      query: ({ domain, messageClass, primaryPoolId, failoverPoolIds, strategy, applySpf }) => ({
        url: `/send-pools/domains/${encodeURIComponent(domain)}/route`,
        method: 'PUT',
        body: { messageClass, primaryPoolId, failoverPoolIds, strategy, applySpf }
      }),
      invalidatesTags: (_res, _err, args) => [
        'SendPools',
        { type: 'DomainRouting', id: String(args?.domain || '') },
        'Domains'
      ]
    }),

    createDomain: builder.mutation({
      query: (domainData) => ({
        url: '/domains',
        method: 'POST',
        body: domainData
      }),
      invalidatesTags: ['Domains']
    }),

    deleteDomain: builder.mutation({
      query: (domain) => ({
        url: `/domains/${encodeURIComponent(domain)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Domains']
    }),

    hardDeleteDomain: builder.mutation({
      query: ({ domain, confirmation }) => ({
        url: `/domains/${encodeURIComponent(domain)}/hard-delete`,
        method: 'POST',
        body: { confirmation }
      }),
      invalidatesTags: ['Domains', 'EmailAccounts', 'Emails']
    }),

    getForwardingDestinations: builder.query({
      query: () => '/domains/forwarding-destinations',
      providesTags: ['ForwardingDestinations']
    }),

    createForwardingDestination: builder.mutation({
      query: ({ name, targetUrl }) => ({
        url: '/domains/forwarding-destinations',
        method: 'POST',
        body: { name, targetUrl }
      }),
      invalidatesTags: ['ForwardingDestinations']
    }),

    updateForwardingDestination: builder.mutation({
      query: ({ destinationId, name, targetUrl }) => ({
        url: `/domains/forwarding-destinations/${encodeURIComponent(destinationId)}`,
        method: 'PUT',
        body: { name, targetUrl }
      }),
      invalidatesTags: ['ForwardingDestinations']
    }),

    deleteForwardingDestination: builder.mutation({
      query: (destinationId) => ({
        url: `/domains/forwarding-destinations/${encodeURIComponent(destinationId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['ForwardingDestinations']
    }),

    updateDomainForwarding: builder.mutation({
      query: ({ domain, forwardingEnabled, forwardingDestinationId, forwardingPreservePath, forwardingPreserveQuery, forwardingStatusCode }) => ({
        url: `/domains/${encodeURIComponent(domain)}/forwarding`,
        method: 'PUT',
        body: {
          forwardingEnabled,
          forwardingDestinationId,
          forwardingPreservePath,
          forwardingPreserveQuery,
          forwardingStatusCode
        }
      }),
      invalidatesTags: ['Domains']
    }),

    applyDomainForwardingDns: builder.mutation({
      query: ({ domain }) => ({
        url: `/domains/${encodeURIComponent(domain)}/forwarding/dns/apply`,
        method: 'POST'
      }),
      invalidatesTags: ['Domains']
    }),

    getDomainForwardingSetup: builder.query({
      query: (domain) => `/domains/${encodeURIComponent(domain)}/forwarding-setup`,
      providesTags: (result, error, domain) => [{ type: 'Domains', id: `forwarding-setup-${domain}` }]
    }),

    checkDomainAvailability: builder.query({
      query: (domain) => `/domains/${domain}/check-availability`,
      providesTags: ['Domains']
    }),

    // Domain Discovery (read-only DNS lookup)
    domainDiscovery: builder.query({
      query: (domain) => `/domains/discovery?domain=${domain}`,
      providesTags: ['Domains']
    }),

    // Route 53 Ownership Check (read-only)
    route53Ownership: builder.query({
      query: ({ domain, accountId }) => {
        const params = new URLSearchParams();
        params.append('domain', domain);
        if (accountId) params.append('accountId', accountId);
        return `/domains/route53-ownership?${params.toString()}`;
      },
      providesTags: ['Domains']
    }),

    // Domain Confirmation (safety check)
    domainConfirmation: builder.mutation({
      query: ({ domain, confirmation }) => ({
        url: '/domains/confirmation',
        method: 'POST',
        body: { domain, confirmation }
      }),
      invalidatesTags: ['Domains']
    }),

    // Email Account Management
    getEmailAccounts: builder.query({
      query: () => '/email-accounts',
      providesTags: ['EmailAccounts'],
      // Force fresh data on each refetch
      keepUnusedDataFor: 0
    }),

    createEmailAccount: builder.mutation({
      query: (accountData) => ({
        url: '/email-accounts',
        method: 'POST',
        body: accountData
      }),
      invalidatesTags: ['EmailAccounts']
    }),

    deleteEmailAccount: builder.mutation({
      query: (emailId) => ({
        url: `/email-accounts/${encodeURIComponent(emailId)}`,
        method: 'DELETE'
      }),
      // If a mailbox is deleted, its emails should disappear immediately (including "All Inboxes").
      invalidatesTags: ['EmailAccounts', 'Emails']
    }),

    putEmailAccount: builder.mutation({
      query: ({ emailId, accountData }) => ({
        url: `/email-accounts/${emailId}`,
        method: 'PUT',
        body: accountData
      }),
      invalidatesTags: ['EmailAccounts']
    }),

    uploadEmailAccountAvatar: builder.mutation({
      query: ({ emailId, file }) => {
        const formData = new FormData();
        formData.append('avatar', file);
        return {
          url: `/email-accounts/${encodeURIComponent(emailId)}/avatar`,
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: ['EmailAccounts']
    }),

    verifyEmailAccount: builder.mutation({
      query: (emailId) => ({
        url: `/email-accounts/${encodeURIComponent(emailId)}/verify`,
        method: 'POST'
      }),
      invalidatesTags: ['EmailAccounts']
    }),

    updateEmailAccount: builder.mutation({
      query: ({ email, ...fields }) => ({
        url: `/email-accounts/${encodeURIComponent(email)}`,
        method: 'PATCH',
        body: fields
      }),
      invalidatesTags: ['EmailAccounts']
    }),

    // Setup email receiving for email accounts
    setupEmailReceiving: builder.mutation({
      query: ({ emailAddresses, accountId, region }) => ({
        url: '/email-accounts/setup-receiving',
        method: 'POST',
        body: { 
          emailAddresses,
          ...(accountId && accountId !== 'all' && { accountId }),
          ...(region && { region })
        }
      }),
      invalidatesTags: ['EmailAccounts']
    }),

    // Setup email receiving for a domain (domain-level; looks up email addresses for domain)
    setupReceivingForDomain: builder.mutation({
      query: ({ domain, region, provider, applyMx }) => ({
        url: `/domains/${encodeURIComponent(domain)}/setup-receiving`,
        method: 'POST',
        body: {
          ...(region ? { region } : {}),
          ...(provider ? { provider } : {}),
          ...(typeof applyMx === 'boolean' ? { applyMx } : {}),
        }
      }),
      invalidatesTags: ['Domains', 'EmailAccounts']
    }),

    // Check email receiving setup
    checkEmailReceivingSetup: builder.query({
      query: ({ emailAddresses, accountId, region }) => {
        const params = new URLSearchParams();
        if (emailAddresses && emailAddresses.length > 0) {
          params.append('emailAddresses', emailAddresses.join(','));
        }
        if (accountId && accountId !== 'all') {
          params.append('accountId', accountId);
        }
        if (region) {
          params.append('region', region);
        }
        return `/email-accounts/check-receiving-setup?${params.toString()}`;
      },
      providesTags: ['EmailAccounts']
    }),

    // Check bounce/complaint handling setup
    checkBounceComplaintSetup: builder.query({
      query: ({ userEmail, accountId, region }) => {
        const params = new URLSearchParams();
        if (userEmail) {
          params.append('email', userEmail);
        }
        if (accountId) params.append('accountId', accountId);
        if (region) params.append('region', region);
        // NOTE: kept as-is to avoid changing behavior during HTTP API canary validation.
        // If this endpoint is wrong, fix in a separate PR after staging validation.
        return `/bounce-complaint-setup?${params.toString()}`;
      },
      providesTags: ['BounceComplaintSetup']
    }),
    checkSandboxExitReadiness: builder.query({
      query: ({ domain, accountId, region }) => {
        if (!domain) {
          throw new Error('Domain is required for sandbox exit readiness check');
        }
        return `/email-accounts/sandbox-exit-readiness/${encodeURIComponent(domain)}`;
      },
      providesTags: ['SandboxExitReadiness']
    }),

    // Setup bounce/complaint handling
    setupBounceComplaintHandling: builder.mutation({
      query: ({ emailAddress, accountId, region }) => ({
        url: '/email-accounts/setup-bounce-complaint-handling',
        method: 'POST',
        body: { 
          emailAddress,
          ...(accountId && accountId !== 'all' && { accountId }),
          ...(region && { region })
        }
      }),
      invalidatesTags: ['EmailAccounts']
    }),
    updateEmail: builder.mutation({
      query: ({ emailId, emailAccount, updates, unifiedPk, unifiedSk }) => ({
        url: '/emails/update',
        method: 'PUT',
        body: { emailId, emailAccount, updates, unifiedPk, unifiedSk }
      }),
      invalidatesTags: ['Emails']
    }),
    purgeTrash: builder.mutation({
      query: () => ({
        url: '/emails/purge-trash',
        method: 'DELETE',
      }),
      invalidatesTags: ['Emails']
    }),
    permanentDeleteEmails: builder.mutation({
      query: ({ emailIds }) => ({
        url: '/emails/permanent-delete',
        method: 'POST',
        body: { emailIds },
      }),
      invalidatesTags: ['Emails']
    }),
    sendEmail: builder.mutation({
      query: (emailData) => ({
        url: '/outbound/emails/send',
        method: 'POST',
        body: emailData
      }),
      invalidatesTags: ['EmailAccounts']
    }),

            // DNS Setup for domain
            getDNSSetup: builder.query({
              query: (domain) => `/domains/${domain}/dns-setup`,
              providesTags: ['Domains']
            }),

            // Domain verification status
            getVerificationStatus: builder.query({
              query: (domain) => `/domains/${domain}/verify-status`,
              providesTags: ['Domains']
            }),

            // Hidden domains (protected domains)
            getHiddenDomains: builder.query({
              query: () => '/domains/hidden',
              providesTags: ['Domains']
            }),

            // Enable DKIM for domain
            enableDKIM: builder.mutation({
              query: ({ domain, accountId }) => ({
                url: `/domains/${domain}/enable-dkim`,
                method: 'POST',
                body: accountId ? { accountId } : {}
              }),
              invalidatesTags: ['Domains']
            }),

            // Configure MAIL FROM domain for SPF alignment
            configureMailFrom: builder.mutation({
              query: ({ domain, mailFromDomain, accountId, region }) => {
                // CRITICAL: Log what we're sending
                const body = { 
                  mailFromDomain
                };
                
                // Always include accountId if provided (don't use conditional spread)
                if (accountId && accountId !== 'all') {
                  body.accountId = accountId;
                }
                
                // Always include region if provided
                if (region) {
                  body.region = region;
                }
                
                console.log(`📤 configureMailFrom API call - domain: ${domain}, body:`, body);
                
                return {
                  url: `/domains/${domain}/configure-mail-from`,
                  method: 'POST',
                  body: body
                };
              },
              invalidatesTags: ['Domains']
            }),

            // Configure custom tracking domain/subdomain (per-domain)
            configureTrackingDomain: builder.mutation({
              query: ({ domain, trackingSubdomain, trackingProvider = 'instantly', accountId, autoApply = true, enabled = true }) => {
                const body = {
                  trackingSubdomain,
                  trackingProvider,
                  autoApply,
                  enabled
                };
                if (accountId && accountId !== 'all') {
                  body.accountId = accountId;
                }
                return {
                  url: `/domains/${encodeURIComponent(domain)}/tracking-domain`,
                  method: 'POST',
                  body
                };
              },
              invalidatesTags: ['Domains']
            }),

            getTrackingDomainStatus: builder.query({
              query: ({ domain, accountId, force }) => {
                const params = new URLSearchParams();
                if (accountId && accountId !== 'all') params.append('accountId', accountId);
                if (force === true) params.append('force', 'true');
                const qs = params.toString();
                return `/domains/${encodeURIComponent(domain)}/tracking-domain/status${qs ? `?${qs}` : ''}`;
              },
              providesTags: ['Domains']
            }),

            // Update DMARC settings
            updateDMARC: builder.mutation({
              query: ({ domain, dmarcEmail, dmarcPolicy, accountId, region }) => {
                const body = { 
                  dmarcEmail,
                  // Safe default: monitor-only (p=none) in BOTH staging + production initially.
                  dmarcPolicy: dmarcPolicy || 'none'
                };
                
                if (accountId && accountId !== 'all') {
                  body.accountId = accountId;
                }
                
                if (region) {
                  body.region = region;
                }
                
                return {
                  url: `/domains/${domain}/update-dmarc`,
                  method: 'POST',
                  body: body
                };
              },
              invalidatesTags: ['Domains']
            }),

            // Validate DNS records (check if they're working)
            validateDNSRecords: builder.mutation({
              query: ({ domain, provider }) => ({
                url: `/domains/${domain}/validate-dns${provider ? `?provider=${encodeURIComponent(provider)}` : ''}`,
                method: 'GET'
              }),
              invalidatesTags: ['Domains']
            }),


    // AWS Connection Test
    testAWSConnection: builder.query({
      query: () => '/aws/test-connection',
      providesTags: ['SES', 'S3']
    }),

    // User Management — pass tenantId for server-side roster scope (company tenant, not AWS).
    getUsers: builder.query({
      query: (arg) => {
        const explicit =
          arg && typeof arg === 'object' && String(arg.tenantId || '').trim()
            ? String(arg.tenantId).trim()
            : '';
        const tenantId =
          explicit && explicit.toLowerCase() !== 'all'
            ? explicit
            : resolveTenantId(arg);
        const q =
          tenantId && tenantId.toLowerCase() !== 'all'
            ? `?tenantId=${encodeURIComponent(tenantId)}`
            : '';
        return `/users${q}`;
      },
      providesTags: ['Users']
    }),

    createUser: builder.mutation({
      query: (userData) => ({
        url: '/users',
        method: 'POST',
        body: userData
      }),
      invalidatesTags: ['Users']
    }),

    updateUser: builder.mutation({
      query: ({ userId, ...userData }) => ({
        url: `/users/${encodeURIComponent(userId)}`, // URL-encode user ID (handles pipe | character)
        method: 'PUT',
        body: userData
      }),
      invalidatesTags: ['Users']
    }),

    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/users/${encodeURIComponent(userId)}`, // URL-encode user ID
        method: 'DELETE'
      }),
      invalidatesTags: ['Users']
    }),

    sendInvitation: builder.mutation({
      query: (userId) => ({
        url: `/users/${encodeURIComponent(userId)}/invite`, // URL-encode user ID
        method: 'POST'
      }),
      invalidatesTags: ['Users']
    }),

    // Self-serve company / tenant provisioning (domain-based signup)
    getSignupStatus: builder.query({
      query: () => '/signup/status',
      transformResponse: (response) => {
        if (response?.success && response?.data !== undefined) return response.data;
        return response?.data ?? response;
      },
      providesTags: ['SignupStatus']
    }),

    resolveCompanySignup: builder.mutation({
      query: (body) => ({
        url: '/signup/resolve-company',
        method: 'POST',
        body
      }),
      transformResponse: (response) => {
        if (response?.success && response?.data !== undefined) return response.data;
        return response?.data ?? response;
      },
      invalidatesTags: ['SignupStatus', 'Users']
    }),

    // User Preferences
    getUserPreferences: builder.query({
      query: (arg) => {
        const tenantId = resolveTenantId(arg);
        return tenantId
          ? `/users/preferences?tenantId=${encodeURIComponent(String(tenantId))}`
          : '/users/preferences';
      },
      providesTags: ['UserPreferences'],
      transformResponse: (response) => {
        // API returns: { success: true, data: { userId: "...", preferences: { uniboxAutoRefresh: false } } }
        console.log('🔍 getUserPreferences - Raw API response:', response);
        if (response?.success && response?.data) {
          console.log('🔍 getUserPreferences - Extracted data:', response.data);
          return response.data; // Return just the data part
        }
        // Fallback: if response structure is different
        if (response?.data) {
          return response.data;
        }
        // If response is already the data structure
        if (response?.preferences || response?.userId) {
          return response;
        }
        console.warn('🔍 getUserPreferences - Unexpected response structure:', response);
        return { preferences: { uniboxAutoRefresh: false } }; // Default
      }
    }),

    updateUserPreferences: builder.mutation({
      query: (arg) => {
        const tenantId = resolveTenantId(arg);
        const preferences = arg?.preferences ?? arg;
        const q = tenantId ? `?tenantId=${encodeURIComponent(String(tenantId))}` : '';
        return {
          url: `/users/preferences${q}`,
          method: 'PUT',
          body: { preferences }
        };
      },
      invalidatesTags: ['UserPreferences'],
      transformResponse: (response) => {
        // API returns: { success: true, data: { userId: "...", preferences: { ... } } }
        console.log('🔍 updateUserPreferences - Raw API response:', response);
        if (response?.success && response?.data) {
          return response.data;
        }
        if (response?.data) {
          return response.data;
        }
        return response;
      }
    }),

    uploadCompanyLogo: builder.mutation({
      query: (arg = {}) => {
        const { file } = arg;
        const tenantId = resolveTenantId(arg);
        const formData = new FormData();
        formData.append('logo', file);
        const q = tenantId ? `?tenantId=${encodeURIComponent(String(tenantId))}` : '';
        return {
          url: `/company-profile/logo${q}`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['UserPreferences'],
    }),

    getUserLoginHistory: builder.query({
      query: (userId) => `/users/${encodeURIComponent(userId)}/login-history`,
      providesTags: ['Users']
    }),

    getUserMFAStatus: builder.query({
      query: (userId) => `/users/${encodeURIComponent(userId)}/mfa-status`,
      providesTags: (result, error, userId) => [{ type: 'Users', id: userId }], // User-specific tags to avoid cache conflicts
      // Don't share cache between different users
      keepUnusedDataFor: 0, // Don't cache unused data to force fresh fetches
      // CRITICAL: Never use cached data for MFA status - always fetch fresh
      // This prevents showing stale "Enabled" status on initial sign-in
      merge: (currentCache, newItems) => {
        // Always prefer fresh data over cache
        return newItems;
      }
    }),

    initiateMFAEnrollment: builder.mutation({
      query: (userId) => ({
        url: `/users/${encodeURIComponent(userId)}/mfa-enroll`,
        method: 'POST',
        body: { type: 'otp' } // 'otp' for TOTP/authenticator apps (Google Authenticator, etc.)
      }),
      invalidatesTags: ['Users']
    }),

    changePassword: builder.mutation({
      query: ({ userId, currentPassword, newPassword }) => ({
        url: `/users/${encodeURIComponent(userId)}/change-password`,
        method: 'POST',
        body: { currentPassword, newPassword }
      }),
      invalidatesTags: ['Users']
    }),
    uploadUserAvatar: builder.mutation({
      query: ({ userId, file }) => {
        const formData = new FormData();
        formData.append('avatar', file);
        return {
          url: `/users/${encodeURIComponent(userId)}/avatar`,
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: ['Users']
    }),

    // AWS Account Management
    getAWSAccounts: builder.query({
      query: () => '/aws-accounts',
      providesTags: ['AWSAccounts'],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      transformResponse: (response) => {
        // API returns: { success: true, data: { accounts: [...], total: N } }
        // RTK Query parses JSON automatically, so response is already an object
        console.log('🔍 getAWSAccounts - Raw API response:', response);
        if (response?.success && response?.data?.accounts) {
          console.log('🔍 getAWSAccounts - Found accounts:', response.data.accounts);
          return response.data;
        }
        // If response structure is different, try to extract accounts
        if (response?.accounts) {
          console.log('🔍 getAWSAccounts - Found accounts in root:', response.accounts);
          return { accounts: response.accounts, total: response.accounts.length };
        }
        if (Array.isArray(response)) {
          console.log('🔍 getAWSAccounts - Response is array:', response);
          return { accounts: response, total: response.length };
        }
        console.warn('🔍 getAWSAccounts - Unexpected response structure:', response);
        return { accounts: [], total: 0 };
      }
    }),

    createAWSAccount: builder.mutation({
      query: (accountData) => ({
        url: '/aws-accounts',
        method: 'POST',
        body: accountData
      }),
      invalidatesTags: ['AWSAccounts']
    }),

    updateAWSAccount: builder.mutation({
      query: ({ accountId, ...accountData }) => ({
        url: `/aws-accounts/${encodeURIComponent(accountId)}`,
        method: 'PUT',
        body: accountData
      }),
      invalidatesTags: ['AWSAccounts']
    }),

    deleteAWSAccount: builder.mutation({
      query: (accountId) => ({
        url: `/aws-accounts/${encodeURIComponent(accountId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['AWSAccounts']
    }),

    // API-only providers removed (out of scope for Kross Global SMTP relay + send pools architecture)

    // CRM - Leads
    getLeads: builder.query({
      query: ({ visibility, ownerId } = {}) => {
        const params = new URLSearchParams();
        if (visibility) params.append('visibility', visibility);
        if (ownerId) params.append('ownerId', ownerId);
        const queryString = params.toString();
        return `/leads${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Leads'],
      transformResponse: (response) => {
        if (response?.success && response?.data?.leads) {
          return response.data.leads;
        }
        if (Array.isArray(response)) {
          return response;
        }
        return [];
      }
    }),

    getLead: builder.query({
      query: (leadId) => `/leads/${encodeURIComponent(leadId)}`,
      providesTags: (result, error, leadId) => [{ type: 'Leads', id: leadId }],
      transformResponse: (response) => {
        if (response?.success && response?.data?.lead) {
          return response.data.lead;
        }
        return response;
      }
    }),

    createLead: builder.mutation({
      query: (leadData) => ({
        url: '/leads',
        method: 'POST',
        body: leadData
      }),
      invalidatesTags: ['Leads']
    }),

    updateLead: builder.mutation({
      query: ({ leadId, ...leadData }) => ({
        url: `/leads/${encodeURIComponent(leadId)}`,
        method: 'PUT',
        body: leadData
      }),
      invalidatesTags: (result, error, { leadId }) => [
        { type: 'Leads', id: leadId },
        'Leads'
      ]
    }),

    deleteLead: builder.mutation({
      query: (leadId) => ({
        url: `/leads/${encodeURIComponent(leadId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Leads']
    }),

    // CRM - Clients
    getClients: builder.query({
      query: ({ visibility, ownerId, status } = {}) => {
        const params = new URLSearchParams();
        if (visibility) params.append('visibility', visibility);
        if (ownerId) params.append('ownerId', ownerId);
        if (status) params.append('status', status);
        const queryString = params.toString();
        return `/clients${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Clients'],
      transformResponse: (response) => {
        if (response?.success && response?.data?.clients) {
          return response.data.clients;
        }
        if (Array.isArray(response)) {
          return response;
        }
        return [];
      }
    }),

    getClient: builder.query({
      query: (clientId) => `/clients/${encodeURIComponent(clientId)}`,
      providesTags: (result, error, clientId) => [{ type: 'Clients', id: clientId }],
      transformResponse: (response) => {
        if (response?.success && response?.data?.client) {
          return response.data.client;
        }
        return response;
      }
    }),

    createClient: builder.mutation({
      query: (clientData) => ({
        url: '/clients',
        method: 'POST',
        body: clientData
      }),
      invalidatesTags: ['Clients']
    }),

    updateClient: builder.mutation({
      query: ({ clientId, ...clientData }) => ({
        url: `/clients/${encodeURIComponent(clientId)}`,
        method: 'PUT',
        body: clientData
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: 'Clients', id: clientId }],
      async onQueryStarted({ clientId, ...patch }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: raw } = await queryFulfilled;
          const serverClient =
            raw?.success && raw?.data?.client
              ? raw.data.client
              : raw?.client ?? raw;
          if (!serverClient || typeof serverClient !== 'object') return;
          const merged = { ...serverClient, ...patch };
          const cachedArgs = marketingApi.util.selectCachedArgsForQuery(getState(), 'getClients');
          for (const args of cachedArgs) {
            dispatch(
              marketingApi.util.updateQueryData('getClients', args, (draft) => {
                if (!Array.isArray(draft)) return;
                const idx = draft.findIndex(
                  (c) => c && (c.id === clientId || String(c.id) === String(clientId))
                );
                if (idx >= 0) Object.assign(draft[idx], merged);
              })
            );
          }
        } catch {
          /* mutation failed */
        }
      },
    }),

    deleteClient: builder.mutation({
      query: (clientId) => ({
        url: `/clients/${encodeURIComponent(clientId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Clients']
    }),

    // Executive Assistant (kg-api-executive-assistant; base URL from getExecutiveAssistantApiUrl)
    getEaAssistants: builder.query({
      query: (arg = {}) => {
        const tenantId = resolveTenantId(arg);
        const params = new URLSearchParams();
        if (tenantId) params.set('tenantId', tenantId);
        const qs = params.toString();
        return `/assistants${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['EaAssistants'],
      transformResponse: (response) => {
        if (response?.success && Array.isArray(response.data?.assistants)) return response.data.assistants;
        return [];
      },
    }),

    getEaTasks: builder.query({
      query: (arg = {}) => {
        const tenantId = resolveTenantId(arg);
        const params = new URLSearchParams();
        if (tenantId) params.set('tenantId', tenantId);
        const qs = params.toString();
        return `/assistant-tasks${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['EaTasks'],
      transformResponse: (response) => {
        if (response?.success && Array.isArray(response.data?.tasks)) return response.data.tasks;
        return [];
      },
    }),

    getEaMemory: builder.query({
      query: (arg = {}) => {
        const tenantId = resolveTenantId(arg);
        const assistantId = String(arg.assistantId || '').trim();
        const params = new URLSearchParams();
        if (tenantId) params.set('tenantId', tenantId);
        if (assistantId) params.set('assistantId', assistantId);
        const qs = params.toString();
        return `/memory${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['EaMemory'],
      transformResponse: (response) => {
        if (response?.success && Array.isArray(response.data?.memory)) return response.data.memory;
        return [];
      },
    }),

    getEaActivity: builder.query({
      query: (arg = {}) => {
        const tenantId = resolveTenantId(arg);
        const params = new URLSearchParams();
        if (tenantId) params.set('tenantId', tenantId);
        const limit = arg.limit != null ? String(arg.limit) : '';
        if (limit) params.set('limit', limit);
        const qs = params.toString();
        return `/activity${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['EaActivity'],
      transformResponse: (response) => {
        if (response?.success && Array.isArray(response.data?.activity)) return response.data.activity;
        return [];
      },
    }),

    getEaScheduledEmails: builder.query({
      query: (arg = {}) => {
        const tenantId = resolveTenantId(arg);
        const params = new URLSearchParams();
        if (tenantId) params.set('tenantId', tenantId);
        const qs = params.toString();
        return `/scheduled-emails${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['EaScheduledEmails'],
      transformResponse: (response) => {
        if (response?.success && Array.isArray(response.data?.scheduledEmails)) return response.data.scheduledEmails;
        return [];
      },
    }),

    createEaAssistant: builder.mutation({
      query: (body) => ({
        url: '/assistants',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EaAssistants'],
    }),

    updateEaAssistant: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/assistants/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['EaAssistants'],
    }),

    createEaTask: builder.mutation({
      query: (body) => ({
        url: '/assistant-tasks',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EaTasks'],
    }),

    updateEaTask: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/assistant-tasks/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['EaTasks'],
    }),

    deleteEaTask: builder.mutation({
      query: ({ id, tenantId }) => {
        const params = new URLSearchParams();
        if (tenantId) params.set('tenantId', tenantId);
        const qs = params.toString();
        return {
          url: `/assistant-tasks/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['EaTasks'],
    }),

    updateEaScheduledEmail: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/scheduled-emails/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['EaScheduledEmails'],
    }),

    appendEaActivity: builder.mutation({
      query: (body) => ({
        url: '/activity',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EaActivity'],
    }),

    createEaScheduledEmail: builder.mutation({
      query: (body) => ({
        url: '/scheduled-emails',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EaScheduledEmails'],
    }),

    getEaOwnerSendingPreferences: builder.query({
      query: (arg = {}) => {
        const tenantId = resolveTenantId(arg);
        const params = new URLSearchParams();
        if (tenantId) params.set('tenantId', tenantId);
        const qs = params.toString();
        return `/owner-sending-preferences${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['EaOwnerSendingPreferences'],
      transformResponse: (response) => {
        if (response?.success && response.data?.preferences) return response.data.preferences;
        return null;
      },
    }),

    updateEaOwnerSendingPreferences: builder.mutation({
      query: (body) => ({
        url: '/owner-sending-preferences',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['EaOwnerSendingPreferences'],
    }),

    getEaScheduleMessageScripts: builder.query({
      query: (arg = {}) => {
        const tenantId = resolveTenantId(arg);
        const params = new URLSearchParams();
        if (tenantId) params.set('tenantId', tenantId);
        const qs = params.toString();
        return `/schedule-message-scripts${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['EaScheduleMessageScripts'],
      transformResponse: (response) => {
        if (response?.success && Array.isArray(response.data?.scripts)) {
          return response.data.scripts.map((s) => ({
            ...s,
            tags: Array.isArray(s.tags) ? s.tags : [],
            category: s.category != null ? String(s.category) : '',
            status: s.status || 'active',
            useCount: Number(s.useCount) || 0,
            useCase: s.useCase || '',
            createdAt: s.createdAt || s.updatedAt || '',
          }));
        }
        return [];
      },
    }),

    createEaScheduleMessageScript: builder.mutation({
      query: (body) => ({
        url: '/schedule-message-scripts',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EaScheduleMessageScripts'],
    }),

    updateEaScheduleMessageScript: builder.mutation({
      query: ({ id, tenantId, ...body }) => {
        const params = new URLSearchParams();
        if (tenantId) params.set('tenantId', tenantId);
        const qs = params.toString();
        return {
          url: `/schedule-message-scripts/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['EaScheduleMessageScripts'],
    }),

    recordEaScheduleMessageScriptUse: builder.mutation({
      query: ({ id, tenantId }) => {
        const params = new URLSearchParams();
        if (tenantId) params.set('tenantId', tenantId);
        const qs = params.toString();
        return {
          url: `/schedule-message-scripts/${encodeURIComponent(id)}/record-use${qs ? `?${qs}` : ''}`,
          method: 'POST',
          body: {},
        };
      },
      invalidatesTags: ['EaScheduleMessageScripts'],
    }),

    deleteEaScheduleMessageScript: builder.mutation({
      query: ({ id, tenantId }) => {
        const params = new URLSearchParams();
        if (tenantId) params.set('tenantId', tenantId);
        const qs = params.toString();
        return {
          url: `/schedule-message-scripts/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['EaScheduleMessageScripts'],
    }),

    // CRM - Accounts
    getAccounts: builder.query({
      query: ({ visibility, ownerId } = {}) => {
        const params = new URLSearchParams();
        if (visibility) params.append('visibility', visibility);
        if (ownerId) params.append('ownerId', ownerId);
        const queryString = params.toString();
        return `/accounts${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Accounts'],
      transformResponse: (response) => {
        if (response?.success && response?.data?.accounts) {
          return response.data.accounts;
        }
        if (Array.isArray(response)) return response;
        return [];
      }
    }),

    getAccount: builder.query({
      query: (accountId) => `/accounts/${encodeURIComponent(accountId)}`,
      providesTags: (result, error, accountId) => [{ type: 'Accounts', id: accountId }],
      transformResponse: (response) => {
        if (response?.success && response?.data?.account) {
          return response.data.account;
        }
        return response;
      }
    }),

    createAccount: builder.mutation({
      query: (accountData) => ({
        url: '/accounts',
        method: 'POST',
        body: accountData
      }),
      invalidatesTags: ['Accounts']
    }),

    updateAccount: builder.mutation({
      query: ({ accountId, ...accountData }) => ({
        url: `/accounts/${encodeURIComponent(accountId)}`,
        method: 'PUT',
        body: accountData
      }),
      invalidatesTags: (result, error, { accountId }) => [
        { type: 'Accounts', id: accountId },
        'Accounts'
      ]
    }),

    deleteAccount: builder.mutation({
      query: (accountId) => ({
        url: `/accounts/${encodeURIComponent(accountId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Accounts']
    }),

    // CRM - Target Accounts (prospect companies the team wants to pursue;
    // separate entity from Accounts and Clients, with optional link pointers).
    getTargetAccounts: builder.query({
      query: ({ visibility, ownerId, status } = {}) => {
        const params = new URLSearchParams();
        if (visibility) params.append('visibility', visibility);
        if (ownerId) params.append('ownerId', ownerId);
        if (status) params.append('status', status);
        const queryString = params.toString();
        return `/target-accounts${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['TargetAccounts'],
      transformResponse: (response) => {
        if (response?.success && response?.data?.targetAccounts) {
          return response.data.targetAccounts;
        }
        if (Array.isArray(response)) return response;
        return [];
      }
    }),

    getTargetAccount: builder.query({
      query: (id) => `/target-accounts/${encodeURIComponent(id)}`,
      providesTags: (result, error, id) => [{ type: 'TargetAccounts', id }],
      transformResponse: (response) => {
        if (response?.success && response?.data?.targetAccount) {
          return response.data.targetAccount;
        }
        return response;
      }
    }),

    createTargetAccount: builder.mutation({
      query: (data) => ({
        url: '/target-accounts',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['TargetAccounts']
    }),

    updateTargetAccount: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/target-accounts/${encodeURIComponent(id)}`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'TargetAccounts', id },
        'TargetAccounts'
      ]
    }),

    deleteTargetAccount: builder.mutation({
      query: (id) => ({
        url: `/target-accounts/${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['TargetAccounts']
    }),

    // CRM - Contacts
    getContacts: builder.query({
      query: ({ visibility, ownerId, clientId } = {}) => {
        const params = new URLSearchParams();
        if (visibility) params.append('visibility', visibility);
        if (ownerId) params.append('ownerId', ownerId);
        if (clientId) params.append('clientId', clientId);
        const queryString = params.toString();
        return `/contacts${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Contacts'],
      transformResponse: (response) => {
        if (response?.success && response?.data?.contacts) {
          return response.data.contacts;
        }
        if (Array.isArray(response)) {
          return response;
        }
        return [];
      }
    }),

    getContact: builder.query({
      query: (contactId) => `/contacts/${encodeURIComponent(contactId)}`,
      providesTags: (result, error, contactId) => [{ type: 'Contacts', id: contactId }],
      transformResponse: (response) => {
        if (response?.success && response?.data?.contact) {
          return response.data.contact;
        }
        return response;
      }
    }),

    createContact: builder.mutation({
      query: (contactData) => ({
        url: '/contacts',
        method: 'POST',
        body: contactData
      }),
      invalidatesTags: ['Contacts']
    }),

    updateContact: builder.mutation({
      query: ({ contactId, ...contactData }) => ({
        url: `/contacts/${encodeURIComponent(contactId)}`,
        method: 'PUT',
        body: contactData
      }),
      invalidatesTags: (result, error, { contactId }) => [
        { type: 'Contacts', id: contactId },
        'Contacts'
      ]
    }),

    deleteContact: builder.mutation({
      query: (contactId) => ({
        url: `/contacts/${encodeURIComponent(contactId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Contacts']
    }),

    // EMM - Rules Engine
    getRulesEngineConfig: builder.query({
      query: (arg) => {
        const tenantId = arg?.tenantId || arg?.accountId;
        return tenantId
          ? `/rules-engine?tenantId=${encodeURIComponent(String(tenantId))}`
          : '/rules-engine';
      },
      transformResponse: (response) => {
        if (response?.success && response?.data?.config) {
          return response.data.config;
        }
        if (response?.data?.config) {
          return response.data.config;
        }
        if (response?.config) {
          return response.config;
        }
        return response;
      }
    }),

    updateRulesEngineConfig: builder.mutation({
      query: (arg) => {
        const tenantId = arg?.tenantId || arg?.accountId;
        const config = arg?.config ?? arg;
        const q = tenantId ? `?tenantId=${encodeURIComponent(String(tenantId))}` : '';
        return {
          url: `/rules-engine${q}`,
        method: 'PUT',
        body: { config }
        };
      },
      transformResponse: (response) => {
        if (response?.success && response?.data?.config) {
          return response.data.config;
        }
        if (response?.data?.config) {
          return response.data.config;
        }
        if (response?.config) {
          return response.config;
        }
        return response;
      }
    }),

    runWarmupDeletionNow: builder.mutation({
      query: (arg) => {
        const tenantId = arg?.tenantId || arg?.accountId;
        const q = tenantId ? `?tenantId=${encodeURIComponent(String(tenantId))}` : '';
        return {
          url: `/rules-engine/warmup-run-now${q}`,
          method: 'POST',
          body: {}
        };
      },
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return response.data;
        }
        if (response?.data) {
          return response.data;
        }
        return response;
      }
    }),

    // CRM - Deals
    getDeals: builder.query({
      query: ({ visibility, ownerId, stage, clientId } = {}) => {
        const params = new URLSearchParams();
        if (visibility) params.append('visibility', visibility);
        if (ownerId) params.append('ownerId', ownerId);
        if (stage) params.append('stage', stage);
        if (clientId) params.append('clientId', clientId);
        const queryString = params.toString();
        return `/deals${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Deals'],
      transformResponse: (response) => {
        if (response?.success && response?.data?.deals) {
          return response.data.deals;
        }
        if (Array.isArray(response)) {
          return response;
        }
        return [];
      }
    }),

    getDeal: builder.query({
      query: (dealId) => `/deals/${encodeURIComponent(dealId)}`,
      providesTags: (result, error, dealId) => [{ type: 'Deals', id: dealId }],
      transformResponse: (response) => {
        if (response?.success && response?.data?.deal) {
          return response.data.deal;
        }
        return response;
      }
    }),

    createDeal: builder.mutation({
      query: (dealData) => ({
        url: '/deals',
        method: 'POST',
        body: dealData
      }),
      invalidatesTags: ['Deals']
    }),

    updateDeal: builder.mutation({
      query: ({ dealId, ...dealData }) => ({
        url: `/deals/${encodeURIComponent(dealId)}`,
        method: 'PUT',
        body: dealData
      }),
      invalidatesTags: (result, error, { dealId }) => [
        { type: 'Deals', id: dealId },
        'Deals'
      ]
    }),

    deleteDeal: builder.mutation({
      query: (dealId) => ({
        url: `/deals/${encodeURIComponent(dealId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Deals']
    }),

    // CRM - Meetings
    getMeetings: builder.query({
      query: ({ visibility, ownerId, startDate, endDate, clientId, contactId, dealId } = {}) => {
        const params = new URLSearchParams();
        if (visibility) params.append('visibility', visibility);
        if (ownerId) params.append('ownerId', ownerId);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (clientId) params.append('clientId', clientId);
        if (contactId) params.append('contactId', contactId);
        if (dealId) params.append('dealId', dealId);
        const queryString = params.toString();
        return `/meetings${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Meetings'],
      transformResponse: (response) => {
        if (response?.success && response?.data?.meetings) {
          return response.data.meetings;
        }
        if (Array.isArray(response)) {
          return response;
        }
        return [];
      }
    }),

    getMeeting: builder.query({
      query: (meetingId) => `/meetings/${encodeURIComponent(meetingId)}`,
      providesTags: (result, error, meetingId) => [{ type: 'Meetings', id: meetingId }],
      transformResponse: (response) => {
        if (response?.success && response?.data?.meeting) {
          return response.data.meeting;
        }
        return response;
      }
    }),

    createMeeting: builder.mutation({
      query: (meetingData) => ({
        url: '/meetings',
        method: 'POST',
        body: meetingData
      }),
      invalidatesTags: ['Meetings', 'Leads']
    }),

    updateMeeting: builder.mutation({
      query: ({ meetingId, ...meetingData }) => ({
        url: `/meetings/${encodeURIComponent(meetingId)}`,
        method: 'PUT',
        body: meetingData
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        { type: 'Meetings', id: meetingId },
        'Meetings',
        'Leads'
      ]
    }),

    deleteMeeting: builder.mutation({
      query: (meetingId) => ({
        url: `/meetings/${encodeURIComponent(meetingId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Meetings', 'Leads']
    }),

    // ERM - Projects
    getProjects: builder.query({
      query: ({ stage, ownerId, archived, scope } = {}) => {
        const params = new URLSearchParams();
        if (stage) params.append('stage', stage);
        if (ownerId) params.append('ownerId', ownerId);
        if (scope) params.append('scope', scope);
        if (archived === true) params.append('archived', 'only');
        else if (archived === 'all') params.append('archived', 'all');
        else if (archived === false) params.append('archived', 'false');
        const queryString = params.toString();
        return `/projects${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['ErmProjects'],
      transformResponse: (response) => {
        if (response?.success && response?.data?.projects) return response.data.projects;
        if (Array.isArray(response)) return response;
        return [];
      }
    }),

    getProject: builder.query({
      query: (projectId) => `/projects/${encodeURIComponent(projectId)}`,
      providesTags: (result, error, projectId) => [{ type: 'ErmProjects', id: projectId }],
      transformResponse: (response) => {
        if (response?.success && response?.data?.project) return response.data.project;
        return response;
      }
    }),

    createProject: builder.mutation({
      query: (projectData) => ({
        url: '/projects',
        method: 'POST',
        body: projectData
      }),
      invalidatesTags: ['ErmProjects']
    }),

    updateProject: builder.mutation({
      query: ({ projectId, ...projectData }) => ({
        url: `/projects/${encodeURIComponent(projectId)}`,
        method: 'PUT',
        body: projectData
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'ErmProjects', id: projectId },
        'ErmProjects'
      ]
    }),

    deleteProject: builder.mutation({
      query: (projectId) => ({
        url: `/projects/${encodeURIComponent(projectId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['ErmProjects']
    }),

    // ERM - Tasks
    getTasks: builder.query({
      query: (arg) => {
        const projectId = typeof arg === 'string' ? arg : arg?.projectId;
        const assignee = typeof arg === 'object' ? arg?.assignee : undefined;
        const scope = typeof arg === 'object' ? arg?.scope : undefined;
        const archived = typeof arg === 'object' ? arg?.archived : undefined;
        const params = new URLSearchParams();
        if (projectId) params.append('projectId', projectId);
        if (assignee) params.append('assignee', assignee);
        if (scope) params.append('scope', scope);
        if (archived === true) params.append('archived', 'only');
        else if (archived === 'all') params.append('archived', 'all');
        else if (archived === false) params.append('archived', 'false');
        const queryString = params.toString();
        return `/tasks${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (result, error, arg) => {
        const projectId = typeof arg === 'string' ? arg : arg?.projectId;
        return [
        'ErmTasks',
        ...(projectId ? [{ type: 'ErmTasks', id: projectId }] : [])
        ];
      },
      transformResponse: (response) => {
        if (response?.success && response?.data?.tasks) return response.data.tasks;
        if (Array.isArray(response)) return response;
        return [];
      }
    }),

    getTask: builder.query({
      query: (taskId) => `/tasks/${encodeURIComponent(taskId)}`,
      providesTags: (result, error, taskId) => [{ type: 'ErmTasks', id: taskId }],
      transformResponse: (response) => {
        if (response?.success && response?.data?.task) return response.data.task;
        return response;
      }
    }),

    createTask: builder.mutation({
      query: (taskData) => ({
        url: '/tasks',
        method: 'POST',
        body: taskData
      }),
      invalidatesTags: ['ErmTasks']
    }),

    updateTask: builder.mutation({
      query: ({ taskId, ...taskData }) => ({
        url: `/tasks/${encodeURIComponent(taskId)}`,
        method: 'PUT',
        body: taskData
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'ErmTasks', id: taskId },
        'ErmTasks'
      ]
    }),

    deleteTask: builder.mutation({
      query: (taskId) => ({
        url: `/tasks/${encodeURIComponent(taskId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['ErmTasks']
    }),

    // Hosting Accounts (AWS connections)
    getEmailPipeAccounts: builder.query({
      query: (ownerId) => {
        const params = new URLSearchParams();
        if (ownerId) params.append('ownerId', ownerId);
        return `/email-pipe-accounts${params.toString() ? `?${params.toString()}` : ''}`;
      },
      providesTags: ['EmailPipeAccounts']
    }),

    getEmailPipeAccount: builder.query({
      query: (id) => `/email-pipe-accounts/${encodeURIComponent(id)}`,
      providesTags: (result, error, id) => [{ type: 'EmailPipeAccounts', id }]
    }),

    createEmailPipeAccount: builder.mutation({
      query: (accountData) => ({
        url: '/email-pipe-accounts',
        method: 'POST',
        body: accountData
      }),
      invalidatesTags: ['EmailPipeAccounts']
    }),

    updateEmailPipeAccount: builder.mutation({
      query: ({ id, ...accountData }) => ({
        url: `/email-pipe-accounts/${encodeURIComponent(id)}`,
        method: 'PUT',
        body: accountData
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'EmailPipeAccounts', id },
        'EmailPipeAccounts'
      ]
    }),

    deleteEmailPipeAccount: builder.mutation({
      query: (id) => ({
        url: `/email-pipe-accounts/${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['EmailPipeAccounts']
    }),

    testEmailPipeAccount: builder.mutation({
      query: (id) => ({
        url: `/email-pipe-accounts/${encodeURIComponent(id)}/test`,
        method: 'POST'
      })
    }),

    // Domain Email Provider Updates
    updateDomainEmailType: builder.mutation({
      query: ({ domain, emailType }) => ({
        url: `/domains/${encodeURIComponent(domain)}/email-type`,
        method: 'PUT',
        body: { emailType }
      }),
      invalidatesTags: ['Domains']
    }),

    updateDomainInboundProvider: builder.mutation({
      query: ({ domain, inboundProvider, inboundProviderConfigId }) => ({
        url: `/domains/${encodeURIComponent(domain)}/inbound-provider`,
        method: 'PUT',
        body: { inboundProvider, inboundProviderConfigId }
      }),
      invalidatesTags: ['Domains']
    }),

    updateDomainOutboundProvider: builder.mutation({
      query: ({ domain, outboundProvider, outboundProviderConfigId }) => ({
        url: `/domains/${encodeURIComponent(domain)}/outbound-provider`,
        method: 'PUT',
        body: { outboundProvider, outboundProviderConfigId }
      }),
      invalidatesTags: ['Domains']
    }),

    updateDomainType: builder.mutation({
      query: ({ domain, domainType, forceProvision }) => ({
        url: `/domains/${encodeURIComponent(domain)}/domain-type`,
        method: 'PUT',
        body: { domainType, ...(forceProvision ? { forceProvision: true } : {}) }
      }),
      invalidatesTags: ['Domains']
    }),

    // Domain connection info (SMTP/IMAP hints + guarded password reveal)
    getDomainConnectionInfo: builder.query({
      query: (domain) => `/domains/${encodeURIComponent(domain)}/connection-info`
    }),

    updateDomainSmtpCredentials: builder.mutation({
      query: ({ domain, smtp_username, smtp_password }) => ({
        url: `/domains/${encodeURIComponent(domain)}/smtp-credentials`,
        method: 'PUT',
        body: { smtp_username, smtp_password }
      })
    }),

    updateDomainImapCredentials: builder.mutation({
      query: ({ domain, imap_password }) => ({
        url: `/domains/${encodeURIComponent(domain)}/imap-credentials`,
        method: 'PUT',
        body: { imap_password }
      }),
      invalidatesTags: (result, err, { domain }) => (err ? [] : [{ type: 'Domains', id: `connection-info-${domain}` }])
    }),

    revealDomainConnectionSecret: builder.mutation({
      query: ({ domain, kind, confirmDomain }) => ({
        url: `/domains/${encodeURIComponent(domain)}/connection-info/reveal`,
        method: 'POST',
        body: { kind, confirmDomain }
      })
    }),

    // Outbound SMTP.com relay credentials (per-domain channel login + password in SSM)
    getOutboundSmtpComCredentials: builder.query({
      query: (domain) => `/domains/${encodeURIComponent(domain)}/outbound/smtpcom-credentials`,
      providesTags: (result, err, domain) => [{ type: 'Domains', id: `smtpcom-${domain}` }],
      transformResponse: (response) => {
        if (response?.success && response?.data) return response.data;
        if (response?.data) return response.data;
        return response;
      }
    }),
    getOutboundSmtpComCredentialsStatus: builder.query({
      query: (domain) => `/domains/${encodeURIComponent(domain)}/outbound/smtpcom-credentials/status`,
      providesTags: (result, err, domain) => [{ type: 'Domains', id: `smtpcom-${domain}` }]
    }),
    putOutboundSmtpComCredentials: builder.mutation({
      query: ({ domain, username, password, scope, relayAccountSuffix }) => ({
        url: `/domains/${encodeURIComponent(domain)}/outbound/smtpcom-credentials`,
        method: 'POST',
        body: { username, password, scope: scope || 'domain', relayAccountSuffix }
      }),
      invalidatesTags: (result, err, { domain }) => [{ type: 'Domains', id: `smtpcom-${domain}` }, 'Domains']
    }),
    revealOutboundSmtpComCredentials: builder.mutation({
      query: ({ domain }) => ({
        url: `/domains/${encodeURIComponent(domain)}/outbound/smtpcom-credentials/reveal`,
        method: 'POST',
        body: {}
      })
    }),

    // Outbound Mailgun relay credentials (per-domain, same pattern as SMTP.com)
    getOutboundMailgunCredentials: builder.query({
      query: (domain) => `/domains/${encodeURIComponent(domain)}/outbound/mailgun-credentials`,
      providesTags: (result, err, domain) => [{ type: 'Domains', id: `mailgun-${domain}` }],
      transformResponse: (response) => {
        if (response?.success && response?.data) return response.data;
        if (response?.data) return response.data;
        return response;
      }
    }),
    getOutboundMailgunCredentialsStatus: builder.query({
      query: (domain) => `/domains/${encodeURIComponent(domain)}/outbound/mailgun-credentials/status`,
      providesTags: (result, err, domain) => [{ type: 'Domains', id: `mailgun-${domain}` }]
    }),
    putOutboundMailgunCredentials: builder.mutation({
      query: ({ domain, username, password, relayAccountSuffix }) => ({
        url: `/domains/${encodeURIComponent(domain)}/outbound/mailgun-credentials`,
        method: 'POST',
        body: { username, password, relayAccountSuffix }
      }),
      invalidatesTags: (result, err, { domain }) => [{ type: 'Domains', id: `mailgun-${domain}` }, 'Domains']
    }),
    configureSendgridDns: builder.mutation({
      query: ({ domain, records, accountId }) => ({
        url: `/domains/${encodeURIComponent(domain)}/configure-sendgrid-dns`,
        method: 'POST',
        body: { records, ...(accountId ? { accountId } : {}) }
      }),
      invalidatesTags: (result, err, { domain }) => (result?.success ? ['Domains'] : [])
    }),
    revealOutboundMailgunCredentials: builder.mutation({
      query: ({ domain }) => ({
        url: `/domains/${encodeURIComponent(domain)}/outbound/mailgun-credentials/reveal`,
        method: 'POST',
        body: {}
      })
    }),

    getDomainMXPreview: builder.query({
      query: ({ domain, inboundProvider }) => {
        const params = new URLSearchParams();
        params.append('domain', domain);
        if (inboundProvider) params.append('inboundProvider', inboundProvider);
        return `/domains/${encodeURIComponent(domain)}/mx-preview?${params.toString()}`;
      },
      providesTags: ['Domains']
    }),

    updateDomainMXRecords: builder.mutation({
      query: ({ domain, inboundProvider, inboundProviderConfigId }) => ({
        url: `/domains/${encodeURIComponent(domain)}/mx-records`,
        method: 'PUT',
        body: { inboundProvider, inboundProviderConfigId }
      }),
      invalidatesTags: ['Domains']
    }),

    // Unified Email Monitoring
    // `mode` controls Overall vs Campaign Health (see backend
    // src/lib/classify-send-kind.ts and docs/md/CAMPAIGN_VS_OVERALL_HEALTH.md).
    // Default 'overall' = true ISP reputation; 'campaign' = list-quality view
    // that excludes warmup, transactional, and untagged rows from BOTH the
    // numerator and denominator.
    getUnifiedEmailMonitoring: builder.query({
      query: ({ domain, accountId, timeRange, mode } = {}) => {
        const params = new URLSearchParams();
        if (domain) params.append('domain', domain);
        if (accountId) params.append('accountId', accountId);
        if (timeRange) params.append('timeRange', timeRange);
        if (mode === 'campaign' || mode === 'overall') params.append('mode', mode);
        return `/monitoring/email-health${params.toString() ? `?${params.toString()}` : ''}`;
      },
      // Cache key includes mode so Overall and Campaign aren't aliased.
      providesTags: (result, error, arg) => [
        'EmailMonitoring',
        { type: 'EmailMonitoring', id: `mode:${(arg && arg.mode) || 'overall'}` },
      ],
    }),

    getDomainEmailStatistics: builder.query({
      query: (domain) => `/monitoring/domains/${encodeURIComponent(domain)}/statistics`,
      providesTags: (result, error, domain) => [{ type: 'EmailMonitoring', id: domain }]
    }),

    // Admin: Email platform service inventory / status
    getEmailPlatformStatus: builder.query({
      query: () => '/admin/email-platform/status'
    })
  })
});

export const {
  useGetEmailsQuery,
  useGetEmailSentStatsQuery,
  useLazyGetEmailsQuery,
  useLazyGetEmailOriginalQuery,
  useLazyGetEmailAttachmentQuery,
  useCheckS3BucketQuery,
  useStoreEmailContentMutation,
  useGetEmailContentQuery,
  useGetDomainsQuery,
  useGetSendPoolsQuery,
  useGetSendPoolProvidersQuery,
  useGetSendPoolEndpointsQuery,
  useLazyGetSendPoolEndpointsQuery,
  useSetSendPoolHealthMutation,
  useCreateSendPoolMutation,
  useUpdateSendPoolMutation,
  useDeleteSendPoolMutation,
  useGetPoolCredentialsQuery,
  useLazyGetPoolCredentialsQuery,
  usePutPoolCredentialsMutation,
  useRevealPoolCredentialsMutation,
  useSetDomainFreezeUntilMutation,
  useGetDomainRoutingPolicyQuery,
  useLazyGetDomainRoutingPolicyQuery,
  useCreateDomainMutation,
  useGetForwardingDestinationsQuery,
  useCreateForwardingDestinationMutation,
  useUpdateForwardingDestinationMutation,
  useDeleteForwardingDestinationMutation,
  useUpdateDomainForwardingMutation,
  useApplyDomainForwardingDnsMutation,
  useGetDomainForwardingSetupQuery,
  useDeleteDomainMutation,
  useHardDeleteDomainMutation,
  useSetDomainRouteMutation,
  useCheckDomainAvailabilityQuery,
  useDomainDiscoveryQuery,
  useRoute53OwnershipQuery,
  useDomainConfirmationMutation,
  useGetDNSSetupQuery,
  useGetVerificationStatusQuery,
  useGetHiddenDomainsQuery,
  useEnableDKIMMutation,
  useUpdateDMARCMutation,
  useValidateDNSRecordsMutation,
  useGetEmailAccountsQuery,
  useCreateEmailAccountMutation,
  useDeleteEmailAccountMutation,
  useUpdateEmailAccountMutation,
  useUploadEmailAccountAvatarMutation,
  useVerifyEmailAccountMutation,
  useSetupEmailReceivingMutation,
  useSetupReceivingForDomainMutation,
  useCheckEmailReceivingSetupQuery,
  useCheckBounceComplaintSetupQuery,
  useCheckSandboxExitReadinessQuery,
  useSetupBounceComplaintHandlingMutation,
  useUpdateEmailMutation,
  usePurgeTrashMutation,
  usePermanentDeleteEmailsMutation,
  useSendEmailMutation,
  useTestAWSConnectionQuery,
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useSendInvitationMutation,
  useGetSignupStatusQuery,
  useResolveCompanySignupMutation,
  useGetUserLoginHistoryQuery,
  useGetUserMFAStatusQuery,
  useInitiateMFAEnrollmentMutation,
  useChangePasswordMutation,
  useUploadUserAvatarMutation,
    useGetAWSAccountsQuery,
    useCreateAWSAccountMutation,
  useUpdateAWSAccountMutation,
    useDeleteAWSAccountMutation,
    useConfigureMailFromMutation,
  useConfigureTrackingDomainMutation,
  useGetTrackingDomainStatusQuery,
  useLazyGetTrackingDomainStatusQuery,
  // (API-only providers removed)
    // CRM - Leads
    useGetLeadsQuery,
    useLazyGetLeadsQuery,
    useGetLeadQuery,
    useCreateLeadMutation,
    useUpdateLeadMutation,
    useDeleteLeadMutation,
    // CRM - Clients
    useGetClientsQuery,
    useLazyGetClientsQuery,
    useGetClientQuery,
    useCreateClientMutation,
    useUpdateClientMutation,
    useDeleteClientMutation,
    // Executive Assistant
    useGetEaAssistantsQuery,
    useGetEaTasksQuery,
    useGetEaMemoryQuery,
    useGetEaActivityQuery,
    useGetEaScheduledEmailsQuery,
    useCreateEaAssistantMutation,
    useUpdateEaAssistantMutation,
    useCreateEaTaskMutation,
    useUpdateEaTaskMutation,
    useDeleteEaTaskMutation,
    useUpdateEaScheduledEmailMutation,
    useAppendEaActivityMutation,
    useCreateEaScheduledEmailMutation,
    useGetEaOwnerSendingPreferencesQuery,
    useUpdateEaOwnerSendingPreferencesMutation,
    useGetEaScheduleMessageScriptsQuery,
    useCreateEaScheduleMessageScriptMutation,
    useUpdateEaScheduleMessageScriptMutation,
    useRecordEaScheduleMessageScriptUseMutation,
    useDeleteEaScheduleMessageScriptMutation,
    // CRM - Accounts
    useGetAccountsQuery,
    useLazyGetAccountsQuery,
    useGetAccountQuery,
    useCreateAccountMutation,
    useUpdateAccountMutation,
    useDeleteAccountMutation,
    // CRM - Target Accounts
    useGetTargetAccountsQuery,
    useLazyGetTargetAccountsQuery,
    useGetTargetAccountQuery,
    useCreateTargetAccountMutation,
    useUpdateTargetAccountMutation,
    useDeleteTargetAccountMutation,
    // CRM - Contacts
    useGetContactsQuery,
    useLazyGetContactsQuery,
    useGetContactQuery,
    useCreateContactMutation,
    useUpdateContactMutation,
    useDeleteContactMutation,
    useGetRulesEngineConfigQuery,
    useUpdateRulesEngineConfigMutation,
    useRunWarmupDeletionNowMutation,
    // CRM - Deals
    useGetDealsQuery,
    useGetDealQuery,
    useCreateDealMutation,
    useUpdateDealMutation,
    useDeleteDealMutation,
    // CRM - Meetings
    useGetMeetingsQuery,
    useGetMeetingQuery,
    useCreateMeetingMutation,
    useUpdateMeetingMutation,
    useDeleteMeetingMutation,
    // ERM - Projects & Tasks
    useGetProjectsQuery,
    useGetProjectQuery,
    useCreateProjectMutation,
    useUpdateProjectMutation,
    useDeleteProjectMutation,
    useGetTasksQuery,
    useGetTaskQuery,
    useCreateTaskMutation,
    useUpdateTaskMutation,
    useDeleteTaskMutation,
    // Hosting Accounts (AWS connections)
    useGetEmailPipeAccountsQuery,
    useGetEmailPipeAccountQuery,
    useCreateEmailPipeAccountMutation,
    useUpdateEmailPipeAccountMutation,
    useDeleteEmailPipeAccountMutation,
    useTestEmailPipeAccountMutation,
    // Domain Email Providers
    useUpdateDomainEmailTypeMutation,
    useUpdateDomainInboundProviderMutation,
    useUpdateDomainOutboundProviderMutation,
  useUpdateDomainTypeMutation,
  useLazyGetDomainConnectionInfoQuery,
  useUpdateDomainSmtpCredentialsMutation,
  useUpdateDomainImapCredentialsMutation,
  useRevealDomainConnectionSecretMutation,
  useGetOutboundSmtpComCredentialsQuery,
  useLazyGetOutboundSmtpComCredentialsQuery,
  usePutOutboundSmtpComCredentialsMutation,
  useRevealOutboundSmtpComCredentialsMutation,
  useLazyGetOutboundMailgunCredentialsQuery,
  usePutOutboundMailgunCredentialsMutation,
  useConfigureSendgridDnsMutation,
  useRevealOutboundMailgunCredentialsMutation,
    useGetDomainMXPreviewQuery,
    useUpdateDomainMXRecordsMutation,
    // Unified Email Monitoring
    useGetUnifiedEmailMonitoringQuery,
    useGetDomainEmailStatisticsQuery,
    // User Preferences
    useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
  useUploadCompanyLogoMutation,
  // Admin: Email platform status
  useGetEmailPlatformStatusQuery
} = marketingApi;
